import React, { useMemo, useState } from "react";
import {
  getExternalIdError,
  normalizeExternalId,
} from "../../utlis/externalId";
import { useT } from "../../i18n/useT";
import { getActiveLocale } from "../../configs/regionalProfile";
import {
  isHeightCentimetersInRange,
  isWeightKilogramsInRange,
  isWeightPoundsInRange,
  feetAndInchesToCentimeters,
  MIN_WEIGHT_KG,
  MAX_WEIGHT_KG,
  MIN_WEIGHT_LBS,
  MAX_WEIGHT_LBS,
} from "../testPerson/measurementUnits";
import { addPatient, isDuplicateIdError } from "../mmtUserList/mmtApi";
import {
  toPersonCreateInput,
  partitionRows,
  rowReasons,
  fieldsForReasons,
  editableFieldsFor,
  fieldLabelKey,
  toDraft,
  draftToCanonical,
  sizeParts,
  sourceDetails,
} from "./importModel";

// Match Add/Edit exactly: gender is a fixed set, birth year spans the same
// 80-year window, so the Fix panel offers the identical choices.
const GENDER_OPTIONS = ["male", "female", "other"];
const YEAR_OPTIONS = Array.from(
  { length: 80 },
  (_, i) => new Date().getFullYear() - 21 - i,
);
const NUMERIC_FIELDS = [
  "heightCm",
  "heightFeet",
  "heightInches",
  "weightKg",
  "weightLb",
];

/**
 * Import Review.
 *
 * The approved v8 flow, unchanged in behaviour:
 *   choose a file -> read -> Review -> rows split into ready / needs attention
 *   -> Fix shows only the fields a row actually needs -> Save commits -> Import.
 *
 * Two rules carried over from v8 and not to be regressed:
 *
 *   1. Editing writes to a per-row draft. The committed row, the ready /
 *      attention split, the counts and the Import total do not move until Save.
 *   2. Close discards the draft. Nothing is restored because nothing was
 *      overwritten.
 *
 * Units come from the regional profile via `usesMetricMeasurements()`. There is
 * no locale test here: the US region shows feet/inches in English, Chinese and
 * Spanish alike, and the Canadian region shows centimetres in French and English
 * alike.
 *
 * `personLabels` is passed in by the caller, which already derives it from the
 * shared terminology mechanism. No second terminology map is created here.
 */
export default function MmtImportReview({
  session,
  rows: extractedRows,
  personLabels,
  market,
  isExternalIdTaken = () => false,
  onImport,
  onCancel,
}) {
  const t = useT("import");
  const tId = useT("externalId");
  // Gender/year/month labels are shared with Add/Edit, so their strings live in
  // the testPersonForm namespace; reuse them rather than duplicating keys.
  const tForm = useT("testPersonForm");
  const monthOptions = Array.from({ length: 12 }, (_, i) => ({
    value: i + 1,
    label: new Date(2000, i, 1).toLocaleString(getActiveLocale(), {
      month: "short",
    }),
  }));

  // Rows come from the extraction of the file the user actually chose.
  const [rows, setRows] = useState(() =>
    (extractedRows || []).map((row) => ({
      ...row,
      extId: normalizeExternalId(row.extId),
    })),
  );
  const [drafts, setDrafts] = useState({}); // rowId -> draft values
  const [openSource, setOpenSource] = useState({});
  const [result, setResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [rowErrors, setRowErrors] = useState({}); // rowId -> i18n key

  const { ready, attention } = useMemo(
    () => partitionRows(rows, isExternalIdTaken),
    [rows, isExternalIdTaken],
  );

  const person = personLabels.singular;
  const people = personLabels.pluralLower;

  const openDraft = (row, only) =>
    setDrafts((d) => ({
      ...d,
      [row._id]: { _only: only, ...toDraft(row, market) },
    }));

  const closeDraft = (row) =>
    setDrafts((d) => {
      const n = { ...d };
      delete n[row._id];
      return n;
    });

  const setDraftField = (row, field, value) =>
    setDrafts((d) => ({ ...d, [row._id]: { ...d[row._id], [field]: value } }));

  /** Save commits the draft; only then does anything recalculate. */
  const saveDraft = (row) => {
    const draft = drafts[row._id];
    if (!draft) return;
    const committed = { ...row, ...draftToCanonical(draft, market) };
    if (
      rowReasons(
        committed,
        rows.map((r) => (r._id === row._id ? committed : r)),
        isExternalIdTaken,
      ).length
    )
      return; // invalid: keep the panel open
    setRows((rs) => rs.map((r) => (r._id === row._id ? committed : r)));
    closeDraft(row);
  };

  /**
   * Live height/weight range validation for the field being edited, matching
   * Add/Edit: a value that is present but out of range gets a red input and an
   * inline hint stating the valid range. Missing values are not flagged here —
   * they simply keep Save disabled, exactly as Add/Edit behave.
   */
  const present = (v) => v !== "" && v !== null && v !== undefined;
  const fieldValidation = (row, field) => {
    const draft = drafts[row._id];
    if (!draft) return { error: false, hint: null };

    if (field === "extId") {
      const reason = getExternalIdError(draft.extId);
      return {
        error: Boolean(reason),
        hint: reason ? tId(reason, { person }) : null,
      };
    }
    if (field === "heightCm" && present(draft.heightCm)) {
      if (!isHeightCentimetersInRange(Number(draft.heightCm)))
        return { error: true, hint: tForm("height_hint_metric") };
    }
    if (
      (field === "heightFeet" || field === "heightInches") &&
      present(draft.heightFeet) &&
      present(draft.heightInches)
    ) {
      const cm = feetAndInchesToCentimeters(
        Number(draft.heightFeet),
        Number(draft.heightInches),
      );
      if (!isHeightCentimetersInRange(cm))
        // Show the hint once (under inches); both inputs still turn red.
        return {
          error: true,
          hint: field === "heightInches" ? tForm("height_hint_imperial") : null,
        };
    }
    if (field === "weightKg" && present(draft.weightKg)) {
      if (!isWeightKilogramsInRange(Number(draft.weightKg)))
        return {
          error: true,
          hint: tForm("weight_hint", {
            min: MIN_WEIGHT_KG,
            max: MAX_WEIGHT_KG,
            unit: tForm("unit_kg"),
          }),
        };
    }
    if (field === "weightLb" && present(draft.weightLb)) {
      if (!isWeightPoundsInRange(Number(draft.weightLb)))
        return {
          error: true,
          hint: tForm("weight_hint", {
            min: MIN_WEIGHT_LBS,
            max: MAX_WEIGHT_LBS,
            unit: tForm("unit_lbs"),
          }),
        };
    }
    return { error: false, hint: null };
  };

  const saveEnabled = (row) => {
    const draft = drafts[row._id];
    if (!draft) return false;
    const candidate = { ...row, ...draftToCanonical(draft, market) };
    return (
      rowReasons(
        candidate,
        rows.map((r) => (r._id === row._id ? candidate : r)),
        isExternalIdTaken,
      ).length === 0
    );
  };

  /**
   * Import for real.
   *
   * Each Ready row is created through the existing `POST /seniors` using the
   * same canonical person payload Add Person uses. Rows are submitted one at a
   * time so a single failure cannot take the batch down, and so each row keeps
   * its own outcome.
   *
   * A row is only removed from the screen once the server has actually created
   * it. Failures stay on screen with their reason — the count never claims a
   * row that did not persist. `submitting` blocks double-submit.
   */
  const confirmImport = async () => {
    if (submitting) return;
    setSubmitting(true);
    const errors = {};
    const created = [];
    const failed = [];

    for (const { row } of ready) {
      const { step1, step2 } = toPersonCreateInput(row);
      try {
        await addPatient(session, step1, step2);
        created.push(row);
      } catch (err) {
        // The server is authoritative on uniqueness; a clean preflight is not
        // proof, so a rejection here is reported as the real outcome.
        errors[row._id] = isDuplicateIdError(err)
          ? "error_duplicate_on_create"
          : "error_create_failed";
        failed.push(row);
      }
    }

    const remaining = [...failed, ...attention.map(({ row }) => row)];
    setRowErrors(errors);
    setRows(remaining);
    setResult({
      imported: created.length,
      failed: failed.length,
      remaining: remaining.length,
    });
    setSubmitting(false);
    if (created.length && onImport) onImport(created);
  };

  const sizeText = (row) => {
    const s = sizeParts(row, market);
    if (s.metric) {
      return [
        s.heightCm == null ? null : `${s.heightCm} cm`,
        s.weightKg == null ? null : `${s.weightKg} kg`,
      ]
        .filter(Boolean)
        .join(" · ");
    }
    return [
      s.feet == null ? null : `${s.feet}'${s.inches}"`,
      s.weightLb == null ? null : `${s.weightLb} lb`,
    ]
      .filter(Boolean)
      .join(" · ");
  };

  const renderField = (row, field) => {
    const draft = drafts[row._id];
    const value = draft[field] ?? "";
    const setVal = (v) => setDraftField(row, field, v);
    const dataAttrs = { "data-imp": row._id, "data-impk": field };
    const { error, hint } = fieldValidation(row, field);

    let control;
    if (field === "gender") {
      // Segmented control, matching Add/Edit.
      control = (
        <div className="mmt-imp-seg" {...dataAttrs}>
          {GENDER_OPTIONS.map((g) => (
            <button
              key={g}
              type="button"
              className={`mmt-imp-seg-btn${value === g ? " selected" : ""}`}
              onClick={() => setVal(value === g ? "" : g)}
            >
              {tForm(g)}
            </button>
          ))}
        </div>
      );
    } else if (field === "birthYear") {
      control = (
        <select
          value={String(value)}
          onChange={(e) => setVal(e.target.value)}
          {...dataAttrs}
        >
          <option value="">{tForm("year")}</option>
          {YEAR_OPTIONS.map((y) => (
            <option key={y} value={y}>
              {y}
            </option>
          ))}
        </select>
      );
    } else if (field === "birthMonth") {
      control = (
        <select
          value={String(value)}
          onChange={(e) => setVal(e.target.value)}
          {...dataAttrs}
        >
          <option value="">{tForm("month")}</option>
          {monthOptions.map((m) => (
            <option key={m.value} value={m.value}>
              {m.label}
            </option>
          ))}
        </select>
      );
    } else if (NUMERIC_FIELDS.includes(field)) {
      control = (
        <input
          type="number"
          inputMode="decimal"
          min="0"
          step="0.1"
          className={error ? "mmt-imp-input-error" : ""}
          value={value}
          onChange={(e) => setVal(e.target.value)}
          {...dataAttrs}
        />
      );
    } else {
      control = (
        <input
          className={error ? "mmt-imp-input-error" : ""}
          aria-invalid={error}
          value={value}
          onChange={(e) => setVal(e.target.value)}
          {...dataAttrs}
        />
      );
    }

    return (
      <label className="mmt-imp-field" key={field}>
        <span>{t(fieldLabelKey(field), { person })}</span>
        {control}
        {hint ? <em className="mmt-imp-hint">{hint}</em> : null}
      </label>
    );
  };

  const renderRow = ({ row, reasons }) => {
    const draft = drafts[row._id];
    const fields = draft
      ? draft._only
        ? fieldsForReasons(reasons, market)
        : editableFieldsFor(market)
      : [];
    const src = sourceDetails(row);
    return (
      <div className="mmt-imp-row" key={row._id}>
        <div className="mmt-imp-line">
          <span className="mmt-imp-name">
            {row.firstName} {row.lastName}
          </span>
          <span className="mmt-imp-id">{row.extId}</span>
          <span className="mmt-imp-size">{sizeText(row)}</span>
          {reasons.length > 0 && (
            <span className="mmt-imp-issue">
              {reasons
                .map((r) =>
                  [
                    "external_id_missing",
                    "external_id_space",
                    "external_id_characters",
                    "external_id_mixed",
                  ].includes(r)
                    ? tId(r, { person })
                    : t(`reason_${r}`, { person }),
                )
                .join(" · ")}
            </span>
          )}
          {rowErrors[row._id] && (
            <span className="mmt-imp-issue" data-imp-error={row._id}>
              {t(rowErrors[row._id], { person })}
            </span>
          )}
          <button
            type="button"
            data-imp-toggle={row._id}
            onClick={() =>
              draft ? closeDraft(row) : openDraft(row, reasons.length > 0)
            }
          >
            {draft ? t("close") : reasons.length ? t("fix") : t("edit")}
          </button>
        </div>

        {draft && (
          <div className="mmt-imp-fix">
            {fields.map((f) => renderField(row, f))}
            <div className="mmt-imp-fix-actions">
              <button
                type="button"
                data-imp-save={row._id}
                disabled={!saveEnabled(row)}
                onClick={() => saveDraft(row)}
              >
                {t("save")}
              </button>
              <button
                type="button"
                onClick={() =>
                  setOpenSource((o) => ({ ...o, [row._id]: !o[row._id] }))
                }
              >
                {openSource[row._id] ? t("hide_source") : t("view_source")}
              </button>
            </div>
            {openSource[row._id] && (
              <div className="mmt-imp-source">
                <p>{t("source_as_supplied")}</p>
                <dl>
                  {Object.entries(src).map(([k, v]) => (
                    <React.Fragment key={k}>
                      <dt>{k}</dt>
                      <dd>{v}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mmt-imp">
      <h1>{t("review_title")}</h1>
      <p className="mmt-imp-summary">
        {result
          ? t("imported_summary", {
              imported: result.imported,
              remaining: result.remaining,
            }) +
            (result.failed
              ? ` · ${t("failed_summary", { failed: result.failed })}`
              : "")
          : t("found_summary", {
              count: rows.length,
              ready: ready.length,
              attention: attention.length,
            })}
      </p>

      {(ready.length > 0 || !result) && (
        <section className="mmt-imp-sec">
          <h3>{t("ready_heading", { count: ready.length })}</h3>
          {ready.length ? ready.map(renderRow) : <p>{t("nothing_ready")}</p>}
        </section>
      )}

      {attention.length > 0 && (
        <section className="mmt-imp-sec">
          <h3>{t("attention_heading", { count: attention.length })}</h3>
          <p>{t("fix_before_import")}</p>
          {attention.map(renderRow)}
        </section>
      )}

      <div className="mmt-imp-actions">
        {ready.length > 0 && (
          <button
            type="button"
            data-action="imp-confirm"
            disabled={submitting}
            onClick={confirmImport}
          >
            {submitting
              ? t("importing")
              : t("import_count", { count: ready.length })}
          </button>
        )}
        <button type="button" data-action="imp-cancel" onClick={onCancel}>
          {result ? t("done") : t("cancel")}
        </button>
      </div>
      <span hidden data-imp-people={people} />
    </div>
  );
}
